<app-modal
    [isOpen]="visibleOpen"
    [title]="modalTitle"
    size="lg"
    [contentClass]="inline ? 'client-fullpage' : ''"
    [inline]="inline"
    [showCloseButton]="!inline"
    [showHeaderLeft]="mode === 'upload' || (mode === 'esign' && esignView === 'list')"
    [showFooter]="mode === 'esign' && esignView === 'sign'"
    footerAlign="right"
    [showCancelButton]="false"
    [showSubmitButton]="false"
    [closeOnBackdropClick]="false"
    (close)="handleClose()">

    <!-- Back button projected into the modal header (left slot) -->
    <!-- Only shown for upload and esign-list views; sign view uses × to go back -->
    <div header-left>
        <button class="btn-back" (click)="goBackToSelect()">
            <hugeicons-icon [icon]="prevIcon" [size]="15"></hugeicons-icon>
            Back
        </button>
    </div>

    <div class="docs-preview-body">

        <!-- ══════════════════════════════════════════════════════════════ -->
        <!-- MODE: SELECT — choose Upload or E-Sign                        -->
        <!-- ══════════════════════════════════════════════════════════════ -->
        @if (mode === 'select') {
        <div class="mode-selection">
            <div class="mode-title">How would you like to submit documents?</div>
            <div class="mode-options">
                <button class="mode-card" (click)="selectMode('upload')">
                    <div class="mode-icon-wrap">
                        <hugeicons-icon [icon]="uploadIcon" [size]="24"></hugeicons-icon>
                    </div>
                    <div class="mode-info">
                        <div class="mode-label">Upload Documents</div>
                        <div class="mode-desc">Select document type and upload a signed PDF file</div>
                    </div>
                    <hugeicons-icon [icon]="nextIcon" [size]="18" class="mode-arrow"></hugeicons-icon>
                </button>
                <button class="mode-card" (click)="selectMode('esign')">
                    <div class="mode-icon-wrap">
                        <hugeicons-icon [icon]="signIcon" [size]="24"></hugeicons-icon>
                    </div>
                    <div class="mode-info">
                        <div class="mode-label">E-Sign Documents</div>
                        <div class="mode-desc">Draw your signature electronically on each document</div>
                    </div>
                    <hugeicons-icon [icon]="nextIcon" [size]="18" class="mode-arrow"></hugeicons-icon>
                </button>
            </div>
        </div>
        }

        <!-- ══════════════════════════════════════════════════════════════ -->
        <!-- MODE: UPLOAD                                                   -->
        <!-- ══════════════════════════════════════════════════════════════ -->
        @if (mode === 'upload') {
        <div class="doc-upload-row">

            <!-- Step hint + radio list + attach: only while there are types remaining -->
            @if (uploadTypeOptions.length > 0) {
            <div class="step-hint">Select a document type, then attach the signed PDF.</div>
            <div class="doc-type-list">
                @for (opt of uploadTypeOptions; track opt.value) {
                <label class="doc-type-item" [class.selected]="selectedUploadType === opt.value">
                    <input type="radio" name="docType" [value]="opt.value"
                        [(ngModel)]="selectedUploadType" class="doc-type-radio">
                    <hugeicons-icon [icon]="fileIcon" [size]="16" class="doc-type-icon"></hugeicons-icon>
                    <span class="doc-type-label">{{ opt.label }}</span>
                    <span class="doc-type-check">
                        <hugeicons-icon [icon]="checkIcon" [size]="18"></hugeicons-icon>
                    </span>
                </label>
                }
            </div>
            <button class="btn btn-primary btn-md choose-btn"
                (click)="triggerFileInput()"
                [disabled]="!selectedUploadType || uploading">
                <hugeicons-icon [icon]="uploadIcon" [size]="16"></hugeicons-icon>
                Attach PDF File
            </button>
            }

            <!-- Queued files list -->
            @if (selectedUploadDocs.length > 0) {
            <div class="selected-documents-list">
                <div class="list-header">
                    Selected
                    <span class="badge">{{ selectedUploadDocs.length }}</span>
                </div>
                @for (doc of selectedUploadDocs; track doc; let i = $index) {
                <div class="selected-doc-item">
                    <hugeicons-icon [icon]="fileIcon" [size]="18" class="file-icon"></hugeicons-icon>
                    <div class="doc-text">
                        <div class="doc-label">{{ doc.label }}</div>
                        <div class="doc-filename">{{ doc.fileName }}</div>
                    </div>
                    <button class="btn-icon-remove" (click)="removeUploadDoc(i)" [disabled]="uploading">
                        <hugeicons-icon [icon]="deleteIcon" [size]="16"></hugeicons-icon>
                    </button>
                </div>
                }
            </div>

            <!-- Submit button — only when all types are queued -->
            @if (allUploadTypesQueued) {
            <button class="btn btn-primary btn-md submit-btn" (click)="submitUploadDocuments()" [disabled]="uploading">
                @if (uploading) {
                <span class="spinner"></span> Uploading…
                } @else {
                <hugeicons-icon [icon]="cloudUploadIcon" [size]="16"></hugeicons-icon>
                Submit Documents
                }
            </button>
            }
            }

            <input type="file" #fileInput (change)="onFileSelected($event)" style="display:none" accept=".pdf">
        </div>
        }

        <!-- ══════════════════════════════════════════════════════════════ -->
        <!-- MODE: ESIGN — LIST VIEW                                        -->
        <!-- ══════════════════════════════════════════════════════════════ -->
        @if (mode === 'esign' && esignView === 'list') {
        <div class="esign-list-section">

            <!-- Loading skeleton while API is in-flight -->
            @if (isLoadingData) {
            <div class="doc-data-loading">
                <div class="doc-data-loading__spinner"></div>
                <p class="doc-data-loading__text">Loading case data&hellip;</p>
            </div>
            }

            @if (!isLoadingData) {
            <div class="esign-documents-list">
                @for (item of esignDocs; track item.index) {
                <div class="esign-doc-card" [class.signed]="item.signed">
                    <div class="esign-doc-card__icon">
                        @if (item.signed) {
                            <hugeicons-icon [icon]="checkIcon" [size]="20"></hugeicons-icon>
                        } @else {
                            <hugeicons-icon [icon]="fileIcon" [size]="20"></hugeicons-icon>
                        }
                    </div>
                    <div class="esign-doc-card__body">
                        <span class="esign-doc-card__status" [class.is-signed]="item.signed">
                            {{ item.signed ? 'Signed' : 'Pending' }}
                        </span>
                        <span class="esign-doc-card__title">{{ item.title }}</span>
                        @if (isSignedOnServer(item.index) && getServerSignedUrl(item.index)) {
                            <a [href]="getServerSignedUrl(item.index)!" target="_blank" rel="noopener"
                               class="esign-doc-card__link">View Signed PDF</a>
                        }
                    </div>
                    <button
                        class="btn btn-sm esign-doc-card__action"
                        [class.btn-primary]="!item.signed"
                        [class.btn-default]="item.signed"
                        (click)="openSignView(item.index)">
                        <hugeicons-icon [icon]="signIcon" [size]="14"></hugeicons-icon>
                        {{ item.signed ? 'Re-sign' : 'Sign' }}
                    </button>
                </div>
                }
            </div>

            <!-- Submit All button — appears once all docs are signed locally -->
            @if (allEsigned) {
            <button class="btn btn-primary btn-md submit-btn" (click)="submitEsignDocuments()" [disabled]="submittingEsign">
                @if (submittingEsign) {
                <span class="spinner"></span> Submitting…
                } @else {
                <hugeicons-icon [icon]="cloudUploadIcon" [size]="16"></hugeicons-icon>
                Submit All Signed Documents
                }
            </button>
            }

            <!-- Share with Client — admin only (hidden on the public client page) -->
            @if (!clientAuth) {
            <div class="share-client-card">
                <div class="share-client-card__icon">
                    <hugeicons-icon [icon]="shareIcon" [size]="22"></hugeicons-icon>
                </div>
                <div class="share-client-card__body">
                    <div class="share-client-card__title">Need the patient to sign?</div>
                    <p class="share-client-card__desc">
                        Send a secure link to your patient so they can review and e-sign these documents directly.
                    </p>
                </div>
                <button
                    class="btn btn-primary btn-sm share-client-card__action"
                    (click)="shareWithClient()"
                    [disabled]="sharingDocument">
                    @if (sharingDocument) {
                        <span class="spinner"></span> Sharing…
                    } @else {
                        <hugeicons-icon [icon]="shareIcon" [size]="14"></hugeicons-icon>
                        Send Link
                    }
                </button>
            </div>
            }
            }
        </div>
        }

        <!-- Inline mode tabs — one per allowed doc, replaces list view navigation -->
        @if (inline && mode === 'esign' && esignView === 'sign' && esignDocs.length > 0) {
        <div class="esign-tabs">
            @for (item of esignDocs; track item.index) {
            <button
                type="button"
                class="esign-tab"
                [class.active]="activeDocIndex === item.index"
                [class.signed]="item.signed"
                (click)="openSignView(item.index)">
                @if (item.signed) {
                <hugeicons-icon [icon]="checkIcon" [size]="14"></hugeicons-icon>
                } @else {
                <hugeicons-icon [icon]="fileIcon" [size]="14"></hugeicons-icon>
                }
                <span>{{ item.title }}</span>
            </button>
            }
        </div>
        }

        <!-- ══════════════════════════════════════════════════════════════ -->
        <!-- MODE: ESIGN — SIGN VIEW (canvas + doc preview)                -->
        <!-- ══════════════════════════════════════════════════════════════ -->
        @if (mode === 'esign' && esignView === 'sign') {

        <!-- Loading skeleton -->
        @if (isLoadingData) {
        <div class="doc-data-loading">
            <div class="doc-data-loading__spinner"></div>
            <p class="doc-data-loading__text">Loading case data&hellip;</p>
        </div>
        }

        <!-- ── DOCUMENT 0 — Acknowledgment of Assignment ── -->
        @if (activeDocIndex === 0 && !isLoadingData) {
        <app-doc-acknowledgment
            [state]="states[0]"
            [caseData]="caseDataPerDoc[0]"
            (downloadPdf)="downloadPdf(0)"
            (clearSig)="clearCanvas(0)"
            (mouseDown)="onMouseDown($event, 0)"
            (mouseMove)="onMouseMove($event, 0)"
            (mouseUp)="onMouseUp(0)"
            (touchStart)="onTouchStart($event, 0)"
            (touchMove)="onTouchMove($event, 0)"
            (touchEnd)="onTouchEnd(0)">
        </app-doc-acknowledgment>
        }

        <!-- ── DOCUMENT 1 — Assignment of Trust Funds ── -->
        @if (activeDocIndex === 1 && !isLoadingData) {
        <app-doc-assignment-trust-funds
            [state]="states[1]"
            [caseData]="caseDataPerDoc[1]"
            (downloadPdf)="downloadPdf(1)"
            (clearPatientSig)="clearCanvas(1)"
            (patMouseDown)="onMouseDown($event, 1)"
            (patMouseMove)="onMouseMove($event, 1)"
            (patMouseUp)="onMouseUp(1)"
            (patTouchStart)="onTouchStart($event, 1)"
            (patTouchMove)="onTouchMove($event, 1)"
            (patTouchEnd)="onTouchEnd(1)">
        </app-doc-assignment-trust-funds>
        }

        <!-- ── DOCUMENT 2 — HIPAA Authorization ── -->
        @if (activeDocIndex === 2 && !isLoadingData) {
        <app-doc-hipaa-authorization
            [state]="states[2]"
            [caseData]="caseDataPerDoc[2]"
            (downloadPdf)="downloadPdf(2)"
            (clearSig)="clearCanvas(2)"
            (mouseDown)="onMouseDown($event, 2)"
            (mouseMove)="onMouseMove($event, 2)"
            (mouseUp)="onMouseUp(2)"
            (touchStart)="onTouchStart($event, 2)"
            (touchMove)="onTouchMove($event, 2)"
            (touchEnd)="onTouchEnd(2)">
        </app-doc-hipaa-authorization>
        }

        <!-- ── DOCUMENT 3 — Release of Lien & Authority to Distribute ── -->
        @if (activeDocIndex === 3 && !isLoadingData) {
        <app-doc-release-of-lien
            [state]="states[3]"
            [caseData]="caseDataPerDoc[3]"
            (downloadPdf)="downloadPdf(3)"
            (clearSig)="clearCanvas(3)"
            (mouseDown)="onMouseDown($event, 3)"
            (mouseMove)="onMouseMove($event, 3)"
            (mouseUp)="onMouseUp(3)"
            (touchStart)="onTouchStart($event, 3)"
            (touchMove)="onTouchMove($event, 3)"
            (touchEnd)="onTouchEnd(3)">
        </app-doc-release-of-lien>
        }

        }<!-- /esign sign view -->

        <!-- ══════════════════════════════════════════════════════════════ -->
        <!-- PROGRESS OVERLAY — shown during PDF generation                  -->
        <!-- ══════════════════════════════════════════════════════════════ -->
        @if (progressPercent !== null) {
        <div class="progress-overlay">
            <div class="progress-card">
                <div class="progress-spinner-wrap">
                    <svg class="progress-ring" viewBox="0 0 48 48">
                        <circle class="progress-ring__bg" cx="24" cy="24" r="20" />
                        <circle class="progress-ring__fill" cx="24" cy="24" r="20"
                            [style.strokeDashoffset]="125.6 - (125.6 * progressPercent / 100)" />
                    </svg>
                    <span class="progress-percent">{{ progressPercent }}%</span>
                </div>
                <p class="progress-label">{{ progressLabel }}</p>
            </div>
        </div>
        }

    </div><!-- /.docs-preview-body -->

    <!-- ── Modal footer: Clear / Download PDF / Submit (only in esign sign view) ── -->
    @if (mode === 'esign' && esignView === 'sign' && !isLoadingData) {
    <div footer class="esign-footer-actions">

        <!-- Doc 0 — Acknowledgment: single patient sig -->
        @if (activeDocIndex === 0) {
        <div class="footer-left">
            <button class="btn btn-md btn-ghost" type="button"
                [disabled]="states[0].isDrawEmpty || submittingCurrentDoc"
                (click)="clearCanvas(0)">
                <hugeicons-icon [icon]="deleteIcon" [size]="15"></hugeicons-icon> Clear
            </button>
        </div>
        <div class="footer-right">
            <button class="btn btn-md btn-primary" type="button"
                [disabled]="states[0].isDrawEmpty || !states[0].selectedDate || submittingCurrentDoc"
                (click)="submitCurrentDoc(0)">
                @if (submittingCurrentDoc) {
                <span class="spinner"></span> Submitting…
                } @else {
                <hugeicons-icon [icon]="cloudUploadIcon" [size]="15"></hugeicons-icon> Submit
                }
            </button>
        </div>
        }

        <!-- Doc 1 — ATF: patient sig only (Factor section is static) -->
        @if (activeDocIndex === 1) {
        <div class="footer-left">
            <button class="btn btn-md btn-ghost" type="button"
                [disabled]="states[1].isDrawEmpty || submittingCurrentDoc"
                (click)="clearCanvas(1)">
                <hugeicons-icon [icon]="deleteIcon" [size]="15"></hugeicons-icon> Clear
            </button>
        </div>
        <div class="footer-right">
            <button class="btn btn-md btn-primary" type="button"
                [disabled]="states[1].isDrawEmpty || !states[1].selectedDate || submittingCurrentDoc"
                (click)="submitCurrentDoc(1)">
                @if (submittingCurrentDoc) {
                <span class="spinner"></span> Submitting…
                } @else {
                <hugeicons-icon [icon]="cloudUploadIcon" [size]="15"></hugeicons-icon> Submit
                }
            </button>
        </div>
        }

        <!-- Doc 2 — HIPAA: single patient sig -->
        @if (activeDocIndex === 2) {
        <div class="footer-left">
            <button class="btn btn-md btn-ghost" type="button"
                [disabled]="states[2].isDrawEmpty || submittingCurrentDoc"
                (click)="clearCanvas(2)">
                <hugeicons-icon [icon]="deleteIcon" [size]="15"></hugeicons-icon> Clear
            </button>
        </div>
        <div class="footer-right">
            <button class="btn btn-md btn-primary" type="button"
                [disabled]="states[2].isDrawEmpty || !states[2].selectedDate || submittingCurrentDoc"
                (click)="submitCurrentDoc(2)">
                @if (submittingCurrentDoc) {
                <span class="spinner"></span> Submitting…
                } @else {
                <hugeicons-icon [icon]="cloudUploadIcon" [size]="15"></hugeicons-icon> Submit
                }
            </button>
        </div>
        }

        <!-- Doc 3 — Release of Lien: single provider sig -->
        @if (activeDocIndex === 3) {
        <div class="footer-left">
            <button class="btn btn-md btn-ghost" type="button"
                [disabled]="states[3].isDrawEmpty || submittingCurrentDoc"
                (click)="clearCanvas(3)">
                <hugeicons-icon [icon]="deleteIcon" [size]="15"></hugeicons-icon> Clear
            </button>
        </div>
        <div class="footer-right">
            <button class="btn btn-md btn-primary" type="button"
                [disabled]="states[3].isDrawEmpty || !states[3].selectedDate || submittingCurrentDoc"
                (click)="submitCurrentDoc(3)">
                @if (submittingCurrentDoc) {
                <span class="spinner"></span> Submitting…
                } @else {
                <hugeicons-icon [icon]="cloudUploadIcon" [size]="15"></hugeicons-icon> Submit
                }
            </button>
        </div>
        }

    </div>
    }

</app-modal>

<!-- Share Success Modal -->
<app-modal
    [isOpen]="showShareSuccessModal"
    [title]="'Document Shared'"
    size="sm"
    [showFooter]="false"
    (close)="closeShareSuccessModal()">
    <div class="share-success-body">
        <hugeicons-icon [icon]="checkIcon" [size]="48" class="share-success-icon"></hugeicons-icon>
        <p class="share-success-text">{{ shareSuccessMessage }}</p>
        <button class="btn btn-primary btn-md" (click)="closeShareSuccessModal()">OK</button>
    </div>
</app-modal>
